import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { createServerFn } from "@tanstack/react-start";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, MessageSquare, ShieldCheck } from "lucide-react";

const SUPPORT_EMAIL = "support@legaldecoder.io";
const SENDER_DOMAIN = "notify.legaldecoder.io";
const FROM_EMAIL = `LegalDecoder Contact <noreply@${SENDER_DOMAIN}>`;

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Please enter a valid email").max(255),
  subject: z.string().trim().min(1, "Subject is required").max(150),
  message: z.string().trim().min(10, "Please add a few more details").max(2000),
});

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const sendContactMessage = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => contactSchema.parse(input))
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const supabaseUrl = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
    const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
    if (!supabaseUrl || !serviceKey) {
      throw new Error("Email service not configured");
    }
    const supabase = createClient(supabaseUrl, serviceKey) as any;

    const messageId = `contact-${crypto.randomUUID()}`;
    const subject = `[Contact] ${data.subject}`;
    const text = [
      `From: ${data.name} <${data.email}>`,
      `Subject: ${data.subject}`,
      "",
      data.message,
    ].join("\n");
    const html = `
      <div style="font-family:system-ui,-apple-system,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#111827">
        <h2 style="margin:0 0 16px">New contact form message</h2>
        <p style="margin:4px 0"><strong>From:</strong> ${escapeHtml(data.name)} &lt;${escapeHtml(data.email)}&gt;</p>
        <p style="margin:4px 0 16px"><strong>Subject:</strong> ${escapeHtml(data.subject)}</p>
        <div style="white-space:pre-wrap;background:#F9FAFB;border:1px solid #E5E7EB;border-radius:8px;padding:16px">${escapeHtml(data.message)}</div>
        <p style="margin-top:24px;font-size:12px;color:#6B7280">Reply directly to ${escapeHtml(data.email)}.</p>
      </div>
    `;

    const { error: logError } = await supabase.from("email_send_log").insert({
      message_id: messageId,
      template_name: "contact-form",
      recipient_email: SUPPORT_EMAIL,
      status: "pending",
    });
    if (logError) console.error("contact log insert failed", logError);

    const { error } = await supabase.rpc("enqueue_email", {
      queue_name: "transactional_emails",
      payload: {
        message_id: messageId,
        to: SUPPORT_EMAIL,
        from: FROM_EMAIL,
        sender_domain: SENDER_DOMAIN,
        subject,
        html,
        text,
        purpose: "transactional",
        label: "contact-form",
        idempotency_key: messageId,
        reply_to: data.email,
      },
    });

    if (error) {
      console.error("enqueue_email failed", error);
      throw new Error("Could not send message right now. Please try again.");
    }

    return { success: true };
  });

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — LegalDecoder" },
      {
        name: "description",
        content:
          "Get in touch with the LegalDecoder team. We typically reply within one business day.",
      },
      { property: "og:title", content: "Contact — LegalDecoder" },
      { property: "og:description", content: "Get in touch with the LegalDecoder team." },
      { property: "og:url", content: "https://legaldecoder.io/contact" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof z.infer<typeof contactSchema>, string>>
  >({});
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    const fd = new FormData(e.currentTarget);
    const raw = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      subject: String(fd.get("subject") ?? ""),
      message: String(fd.get("message") ?? ""),
    };
    const parsed = contactSchema.safeParse(raw);
    if (!parsed.success) {
      const fieldErrors: typeof errors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof typeof errors;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setSubmitting(true);
    try {
      await sendContactMessage({ data: parsed.data });
      setSent(true);
      toast.success("Message sent — we'll get back to you within 1 business day.");
      (e.target as HTMLFormElement).reset();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
      router.invalidate();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <span className="text-xs font-bold uppercase tracking-wider text-primary">
          We've got your back
        </span>
        <h1 className="mt-3 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
          Get in touch.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Questions about a contract, your subscription, or partnerships? Send us a note and we'll
          reply within one business day.
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
            <Mail className="mt-0.5 h-5 w-5 text-primary" />
            <div>
              <div className="text-sm font-semibold text-foreground">Email</div>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-sm text-muted-foreground hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
            <MessageSquare className="mt-0.5 h-5 w-5 text-primary" />
            <div>
              <div className="text-sm font-semibold text-foreground">Reply time</div>
              <div className="text-sm text-muted-foreground">Within 1 business day</div>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
            <ShieldCheck className="mt-0.5 h-5 w-5 text-primary" />
            <div>
              <div className="text-sm font-semibold text-foreground">Private</div>
              <div className="text-sm text-muted-foreground">Never shared or sold</div>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-10 space-y-5 rounded-2xl border border-border bg-card p-6 sm:p-8"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label="Your name"
              name="name"
              autoComplete="name"
              maxLength={100}
              error={errors.name}
            />
            <Field
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              maxLength={255}
              error={errors.email}
            />
          </div>
          <Field label="Subject" name="subject" maxLength={150} error={errors.subject} />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="message">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              rows={6}
              maxLength={2000}
              required
              className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-[15px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              placeholder="Tell us what's on your mind…"
            />
            {errors.message && <p className="mt-1 text-xs text-destructive">{errors.message}</p>}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-muted-foreground">
              By submitting, you agree to our privacy policy. Not legal advice.
            </p>
            <Button type="submit" variant="hero" size="lg" disabled={submitting || sent}>
              {sent ? "Sent ✓" : submitting ? "Sending…" : "Send message"}
            </Button>
          </div>
        </form>

        <p className="mt-12 text-xs uppercase tracking-wider text-muted-foreground">
          Not legal advice. For informational purposes only.
        </p>
      </main>
      <Footer />
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  autoComplete,
  maxLength,
  error,
}: {
  label: string;
  name: string;
  type?: string;
  autoComplete?: string;
  maxLength?: number;
  error?: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        autoComplete={autoComplete}
        maxLength={maxLength}
        required
        className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-[15px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      />
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
